import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { FaqJsonLd } from '../components/FaqJsonLd';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  MapPin,
  Drop,
  Plant,
  Receipt,
  Warning,
} from '@phosphor-icons/react';
import { CITIES } from '../data/regionalCities';

/**
 * /lawn-care/{citySlug} — regional SEO landing page.
 *
 * Persona: useful-first, regular-guy voice. NO branded SaaS pitch in the
 * hero. The Memorial Day Showcase callout (Round Rock only) is the one
 * place we lean into the founder story. Other cities point at the
 * methodology page and the gear list.
 */
export default function RegionalLawnCarePage() {
  const { citySlug } = useParams();
  const city = CITIES[citySlug];

  if (!city) {
    return (
      <div className="min-h-screen bg-zinc-50 px-6 py-24 text-center">
        <h1 className="text-3xl font-black text-zinc-900">City not found</h1>
        <p className="mt-3 text-zinc-600">
          We don't have a guide for that city yet.
        </p>
        <Link to="/" className="mt-6 inline-block">
          <Button>Back to home</Button>
        </Link>
      </div>
    );
  }

  const canonical = `https://getturfpulse.com/lawn-care/${city.slug}`;
  const seoTitle = `${city.name}, ${city.state} Lawn Care — local soil, water rules & schedule`;
  const seoDesc = city.subhead.slice(0, 155);

  // FAQ schema items — drives Google FAQ rich snippets.
  const faqItems = [
    {
      q: `When should I water my lawn in ${city.name}, ${city.state}?`,
      a: `${city.name} sits under ${city.water_restrictions.stage}. ${city.water_restrictions.key_rules[0]} ${city.water_restrictions.key_rules[1] || ''} Per TAMU AgriLife, the most efficient watering window is before 10 AM — which happens to align with the city's mandatory cutoff.`,
    },
    {
      q: `What grass works best in ${city.name}?`,
      a: `${city.name} homeowners typically run ${city.common_grass.join(', ')}. Soil type is a bigger driver than grass type — see the soil section below for what's actually under your lawn.`,
    },
    {
      q: `How much water does my lawn need in ${city.name}?`,
      a: `Bermuda needs roughly 1.0–1.5 inches per week during peak summer in this climate. With Stage 2 restrictions limiting watering days, the math has to fit your allowed window — see the runtime guide below.`,
    },
    {
      q: `Are there water rebates available in ${city.name}?`,
      a: city.rebates.length > 0
        ? `Yes — ${city.rebates[0].name} (${city.rebates[0].amount}). ${city.rebates[0].notes}`
        : `Check with your local utility. Programs change yearly.`,
    },
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 to-white"
      data-testid={`regional-lawn-care-page-${city.slug}`}
    >
      <SEO title={seoTitle} description={seoDesc} canonical={canonical} />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Lawn Care Guides', path: '/lawn-care' },
          { label: `${city.name}, ${city.state}`, path: `/lawn-care/${city.slug}` },
        ]}
      />
      <FaqJsonLd items={faqItems} />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-4xl px-6 py-14">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
            <MapPin size={14} weight="fill" /> {city.name.toUpperCase()},{' '}
            {city.state.toUpperCase()} · USDA Zone {city.usda_zone}
          </div>
          <h1
            className="mt-4 text-4xl font-black tracking-tight text-zinc-900 sm:text-5xl"
            data-testid={`hero-title-${city.slug}`}
          >
            {city.headline}
          </h1>
          <p className="mt-5 text-lg text-zinc-700 leading-relaxed">
            {city.subhead}
          </p>
          {city.bullet_intro &&
            city.bullet_intro.map((line, i) => (
              <p key={i} className="mt-3 text-base text-zinc-600 leading-relaxed">
                {line}
              </p>
            ))}
        </div>
      </section>

      {/* ── This Week's Call ─────────────────────────────────── */}
      <section className="border-b border-zinc-200 bg-emerald-50/40">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <div className="flex items-center gap-2">
            <Plant size={26} className="text-emerald-700" weight="duotone" />
            <h2 className="text-2xl font-black text-zinc-900">
              {city.this_week_call.headline}
            </h2>
          </div>
          <ul
            className="mt-5 space-y-3"
            data-testid={`this-week-call-${city.slug}`}
          >
            {city.this_week_call.bullets.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-zinc-700 leading-relaxed"
              >
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-600" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Soil & Climate ──────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-6 py-14">
        <h2 className="text-3xl font-black text-zinc-900">
          Your dirt (and why it matters)
        </h2>
        <p className="mt-3 text-zinc-700 leading-relaxed">
          {city.soil_type}
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <Card>
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                Annual rainfall
              </div>
              <div className="mt-1 text-2xl font-black text-zinc-900">
                {city.annual_rainfall_in}″
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                USDA Zone
              </div>
              <div className="mt-1 text-2xl font-black text-zinc-900">
                {city.usda_zone}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                Population
              </div>
              <div className="mt-1 text-2xl font-black text-zinc-900">
                {city.population}
              </div>
            </CardContent>
          </Card>
        </div>

        <h3 className="mt-10 text-xl font-bold text-zinc-900">
          Common grasses in {city.name}
        </h3>
        <ul className="mt-3 space-y-2">
          {city.common_grass.map((g, i) => (
            <li key={i} className="flex items-start gap-2 text-zinc-700">
              <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-zinc-400" />
              {g}
            </li>
          ))}
        </ul>
      </section>

      {/* ── Water Restrictions ──────────────────────────────── */}
      <section className="border-y border-zinc-200 bg-sky-50/40">
        <div className="mx-auto max-w-4xl px-6 py-14">
          <div className="flex items-center gap-2">
            <Drop size={26} className="text-sky-700" weight="duotone" />
            <h2 className="text-3xl font-black text-zinc-900">
              {city.name} water restrictions
            </h2>
          </div>
          <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800">
            <Warning size={14} weight="fill" /> {city.water_restrictions.stage}
          </div>
          <ul
            className="mt-5 space-y-3"
            data-testid={`water-restrictions-${city.slug}`}
          >
            {city.water_restrictions.key_rules.map((rule, i) => (
              <li key={i} className="flex items-start gap-3 text-zinc-700">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-600" />
                <span>{rule}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm italic text-zinc-600">
            Restrictions change. Always confirm at the source:{' '}
            <a
              href={city.water_restrictions.official_link}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-sky-700 underline"
              data-testid={`official-water-link-${city.slug}`}
            >
              {city.water_restrictions.official_label} →
            </a>
          </p>
        </div>
      </section>

      {/* ── Rebates ─────────────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-6 py-14">
        <div className="flex items-center gap-2">
          <Receipt size={26} className="text-emerald-700" weight="duotone" />
          <h2 className="text-3xl font-black text-zinc-900">
            Money back from {city.name}
          </h2>
        </div>
        <p className="mt-3 text-zinc-700">
          Smart controllers and conservation gear pay for themselves twice —
          once via your water bill, once via these rebates.
        </p>
        <div className="mt-6 space-y-4">
          {city.rebates.map((r, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="text-lg font-bold text-zinc-900">{r.name}</h3>
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-700">
                    {r.amount}
                  </span>
                </div>
                <p className="mt-2 text-sm text-zinc-700 leading-relaxed">
                  {r.notes}
                </p>
                <a
                  href={r.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-emerald-700 underline"
                  data-testid={`rebate-link-${city.slug}-${i}`}
                >
                  Apply / details <ArrowRight size={12} weight="bold" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Memorial Day Showcase callout (Round Rock only) ──── */}
      {city.showcase_callout && (
        <section
          className="border-y border-amber-300 bg-gradient-to-br from-amber-50 to-emerald-50/40"
          data-testid={`showcase-callout-${city.slug}`}
        >
          <div className="mx-auto max-w-4xl px-6 py-14">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">
              The Memorial Day Showcase · Day-by-day field receipts
            </div>
            <h2 className="mt-3 text-3xl font-black text-zinc-900">
              A real {city.name} lawn, three buried probes, one experiment
            </h2>
            <p className="mt-4 text-zinc-700 leading-relaxed">
              The TurfPulse founder lives here. He buried three soil moisture
              probes in his Bermuda lawn, mapped them to his Rachio zones, and
              is documenting every irrigation decision from May 1 through
              Memorial Day weekend. The rule: <em>"my sensors tell the
              controller when to water."</em>
            </p>
            <p className="mt-3 text-zinc-700 leading-relaxed">
              All the math is open. The gear list (with affiliate links) is
              public. The water bill receipts will be too. If you live in{' '}
              {city.name} and you want to see what your lawn could look like by
              August 1 if you stopped firing on a calendar — read the
              methodology page below.
            </p>
            <Link to="/methodology/probe-driven-irrigation">
              <Button
                className="mt-5 bg-amber-600 hover:bg-amber-700"
                data-testid={`showcase-cta-${city.slug}`}
              >
                See the open methodology <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* ── Soft methodology footer (every city) ─────────────── */}
      <section className="bg-zinc-900 text-zinc-100">
        <div className="mx-auto max-w-4xl px-6 py-14">
          <h2 className="text-2xl font-black">
            How to actually run a smart lawn in {city.name}
          </h2>
          <p className="mt-3 text-zinc-300 leading-relaxed">
            The hardest part of {city.name} lawn care isn't the schedule — it's
            knowing whether your soil actually <em>needs</em> water on the day
            the city allows you to water. The cheapest fix: bury a soil
            moisture probe. The math for translating its reading into runtime
            is open and free:
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/methodology/probe-driven-irrigation">
              <Button
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid={`methodology-cta-${city.slug}`}
              >
                Probe-driven irrigation methodology{' '}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/water-budget">
              <Button
                variant="outline"
                className="border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-800"
                data-testid={`water-budget-cta-${city.slug}`}
              >
                Water Budget calculator
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-xs text-zinc-500 italic">
            Affiliate disclosure: links to gear on this page may earn a small
            commission at no extra cost to you. The math and methodology stay
            free regardless.
          </p>
        </div>
      </section>

      {/* ── FAQ section (visible) ────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-6 py-14">
        <h2 className="text-3xl font-black text-zinc-900">
          {city.name} lawn care FAQ
        </h2>
        <div className="mt-6 space-y-4">
          {faqItems.map((item, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <h3 className="font-bold text-zinc-900">{item.q}</h3>
                <p className="mt-2 text-sm text-zinc-700 leading-relaxed">
                  {item.a}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
